<script lang="ts" setup>
import type { SubjectCode } from '~/models/Subject'
import type { Group } from '~/models/Group'
import type { ContentProductPackage } from '~/models/Content/ContentProductPackage'
import { useRouter }  from '#imports'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { DateTime } from 'luxon'
import { faArrowTurnRight } from '@fortawesome/duotone-regular-svg-icons'
import useProductStore from '~/stores/product'
import { PendoFeature } from '~/models/Pendo'
import { LocationType, TaskStatus } from '~/models/AssignTask'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import { useAssignTask } from '~/composables/useAssignTask'
import { EXAM_TAG } from '~/composables/useAssignTags'
import Select from '~/components/ui/Select.vue'
import Button from '~/components/ui/Button.vue'
import AuSpinner from '~/components/ui/AuSpinner.vue'
import AuDialog from '~/components/ui/AuDialog.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import GroupSelectionDropdown from '~/components/products/exams/assignment/GroupSelectionDropdown.vue'

const {
  resource,
  taskLocationIds
} = defineProps<{
  resource: ContentProductPackage
  taskLocationIds?: number[]
}>()

const router = useRouter()
const { t } = useI18n()
const { createTask: create } = useAssignTask()
const { getProductFromResource } = useProductStore()
const { resolveBaseUrl } = useUrlBuilder()

const open = ref(false)
const selectedGroup = ref<Group>()
const selectedSubject = ref<SubjectCode>()

const startDate = DateTime.local().startOf('week').startOf('day').startOf('second').toUTC().set({ millisecond: 0 })

const product = computed(() => getProductFromResource(resource))

const createTask = async () => {
  if (!selectedGroup.value)
    return

  await create.mutateAsync({
    name: resource.title,
    groups: [selectedGroup.value.groupId],
    grades: resource.grades,
    status: TaskStatus.Closed,
    subjects: resource.subjects?.length
      ? resource.subjects
      : selectedSubject.value ? [selectedSubject.value] : [],
    locationIds: [resource.locationId],
    taskLocationIds: taskLocationIds?.length ? taskLocationIds : [],
    locationType: LocationType.ParentLocation,
    pathString: product.value?.aunivers.pathString,
    tags: [EXAM_TAG],
    startDate,
  })
  open.value = false
  if (router.currentRoute.value.name !== 'product' && product.value) {
    await router.replace({ path: resolveBaseUrl(product.value) })
  }
  window.scrollTo(0, 0)
}
</script>

<template>
  <AuDialog
    v-model:open="open"
    :title="t('termExam.byline', { title: resource.title.toLowerCase() })"
    size="lg"
  >
    <template #trigger>
      <Button
        color="blue"
        shade="light"
        size="xl"
      >
        <font-awesome-icon :icon="faArrowTurnRight" />
        {{ t('assign.createExam') }}
      </Button>
    </template>
    <template #content>
      <div class="flex flex-col gap-4">
        <GroupSelectionDropdown v-model="selectedGroup" />
        <label
          v-if="!resource.subjects?.length && product"
          class="flex flex-col gap-2"
        >
          <span
            class="text-base font-medium"
            v-text="t('filters.chooseSubject')"
          />
          <Select
            v-model="selectedSubject"
            :option-label="(subjectCode: SubjectCode) => t(`metadata.subjects.${subjectCode}`)"
            :options="product.subjects"
            :placeholder="t('filters.chooseSubject')"
            variant="outline"
            size="lg"
          />
        </label>
        <p v-text="t('assign.createInfo1')" />
        <p v-text="t('assign.createInfo2')" />
        <AuCallout
          v-if="create.isError.value"
          :title="t('assign.createError')"
          variant="error"
        />
        <div class="mt-6 flex items-center justify-end gap-4">
          <Button
            :disabled="create.isPending.value"
            color="gray"
            size="lg"
            variant="outline"
            @click="open = !open"
          >
            {{ t('cancel') }}
          </Button>
          <Button
            :data-pendo="PendoFeature.TermExam.CreateTask"
            :disabled="!selectedGroup || create.isPending.value || (!resource.subjects?.length && !selectedSubject)"
            size="lg"
            @click="createTask"
          >
            <div class="flex gap-1">
              {{ create.isPending.value ? t('assign.creatingExam') : t('assign.createExam') }}
              <AuSpinner
                v-if="create.isPending.value"
                size="sm"
                color="var(--color-seagreen-30)"
              />
            </div>
          </Button>
        </div>
      </div>
    </template>
  </AuDialog>
</template>
